import { FC } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  ApprovalErrorFields,
  BookingApproveFormFieldsEnum,
  ModalContainerStyled,
  ModalContentStyled,
  ModalFooterStyled,
  ModalHeader,
  MuiButton,
  useApprovalErrorFields,
} from "@ryde/controls";
import { useYupValidationResolver } from "@ryde/redux";
import {
  BookingCellId,
  BookingConflictErrorType,
  bulkApprovalBookingConflictsAtom,
  IApprovalBookingConflict,
  IDialogContainerProps,
  Log,
  SimpleResult,
  simpleResultFalse,
  TResolveFn,
} from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

import { bookingBulkSetConflictApprovalReasonValidationSchema } from "./booking-bulk-set-conflict-approval-reason.validation";

interface IPassengerConflictsThatWillBeApproved {
  name: string;
  conflicts: BookingConflictErrorType[];
}

const getOtherAndUniqConflictsThatWillBeApproved = ({
  bulkApprovalBookingConflicts,
  bookingCellsIds,
}: {
  bulkApprovalBookingConflicts: Map<BookingCellId, IApprovalBookingConflict[]>;
  bookingCellsIds: BookingCellId[];
}): IPassengerConflictsThatWillBeApproved[] => {
  const passengersIdsConflictsDictionary = new Map<
    number,
    IPassengerConflictsThatWillBeApproved
  >();

  const bookingCellsIdsSet = new Set(bookingCellsIds);
  bulkApprovalBookingConflicts.forEach((bookingConflicts, cellId) => {
    if (bookingCellsIdsSet.has(cellId)) {
      bookingConflicts.forEach(bookingConflict => {
        const { errorType, passengerId, passengerFullName } = bookingConflict;

        const passengerConflictsInfo =
          passengersIdsConflictsDictionary.get(passengerId);

        if (
          passengerConflictsInfo &&
          !passengerConflictsInfo?.conflicts?.includes(errorType)
        ) {
          passengerConflictsInfo.conflicts.push(errorType);
          passengersIdsConflictsDictionary.set(
            passengerId,
            passengerConflictsInfo,
          );
        }

        if (!passengerConflictsInfo) {
          passengersIdsConflictsDictionary.set(passengerId, {
            name: passengerFullName,
            conflicts: [errorType],
          });
        }
      });
    }
  });

  return Array.from(passengersIdsConflictsDictionary.values());
};

interface IBookingBulkSetConflictApprovalReasonMultiPassengersDialog extends IDialogContainerProps {
  bookingCellsIds: BookingCellId[];
  resolveFn: TResolveFn;
}

export const BookingBulkSetConflictApprovalReasonMultiPassengersDialog: FC<
  IBookingBulkSetConflictApprovalReasonMultiPassengersDialog
> = ({ onClose, bookingCellsIds, resolveFn }) => {
  const resolver = useYupValidationResolver(
    bookingBulkSetConflictApprovalReasonValidationSchema,
  );
  const methods = useForm({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {},
    resolver,
  });
  const {
    getValues,
    setValue,
    formState: { isValid },
  } = methods;
  const { t } = useTranslation();
  const bulkApprovalBookingConflicts = useRecoilValue(
    bulkApprovalBookingConflictsAtom,
  );

  const passengerConflictsThatWillBeApproved =
    getOtherAndUniqConflictsThatWillBeApproved({
      bulkApprovalBookingConflicts,
      bookingCellsIds,
    });

  const { reasonCategoryDropdownItems, reasonDropdownItems } =
    useApprovalErrorFields({ getValues, setValue });

  const onCancel = (): void => {
    resolveFn(simpleResultFalse);
    onClose();
  };

  const onApprove = (): void => {
    if (!isValid) {
      Log.errorAndToast(t("errors.invalidBookingApproval"));
      return;
    }

    const values = getValues();

    const approvalReasonToResult = reasonDropdownItems.find(
      ({ value }) =>
        Number(value) ===
        Number(values[BookingApproveFormFieldsEnum.APPROVAL_REASON_ID]),
    );
    const approver = values[BookingApproveFormFieldsEnum.APPROVER];

    resolveFn(
      new SimpleResult({
        success: true,
        data: {
          approvalReason: approvalReasonToResult,
          approver,
        },
      }),
    );
    Log.successAndToast(t("booking.APPROVAL_REASON_APPROVED_SUCCESSFULLY"));
    onClose();
  };

  return (
    <ModalContainerStyled>
      <ModalHeader onClose={onClose}>
        {t("booking.APPROVAL_REASON")}
      </ModalHeader>
      <ModalContentStyled $showContent>
        <header>
          {Boolean(passengerConflictsThatWillBeApproved.length) &&
            passengerConflictsThatWillBeApproved.map(
              ({ name, conflicts }: IPassengerConflictsThatWillBeApproved) => (
                <>
                  <h4 className="ApprovalReasonDialog-Title PassengersDialog">
                    <Trans
                      i18nKey="booking.APPROVE_DUE_THE_REASON"
                      components={{
                        passenger: (
                          <span style={{ color: rydeColors.purpleTitle }} />
                        ),
                      }}
                      values={{ passengerFullName: name }}
                    />
                  </h4>

                  {conflicts.length > 0 && (
                    <ul className="ApprovalReasonDialog-Conflicts">
                      {conflicts.map((conflict, i) => (
                        <li key={`${conflict}_${i}`}>
                          {t(`booking.conflictApprove.${conflict}`)}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              ),
            )}
        </header>
        <FormProvider {...methods}>
          <ApprovalErrorFields
            reasonDropdownItems={reasonDropdownItems}
            reasonCategoryDropdownItems={reasonCategoryDropdownItems}
          />
        </FormProvider>
      </ModalContentStyled>
      <ModalFooterStyled>
        <MuiButton muiVariant="outlined" onClick={onCancel}>
          {t("general.CANCEL")}
        </MuiButton>
        <MuiButton onClick={onApprove} disabled={!isValid}>
          {t("general.APPROVE")}
        </MuiButton>
      </ModalFooterStyled>
    </ModalContainerStyled>
  );
};
